import { sanitizeCssColor, sanitizeFontFamily, sanitizePlainText, sanitizeRichText, sanitizeUrl } from './sanitize.js';
import { BLOCK_TYPES, DESIGN_EXTRA_TYPES } from './blocks.js';
import { normalizeBusiness } from './business.js';
import { isValidDesignText, isValidDesignVar, normalizeDesignExtras } from './design.js';
import designManifestData from './templates/designs.json' with { type: 'json' };
import { defaultTheme, isCssLength } from './theme.js';
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function addError(errors, path, message, code = 'VALIDATION_ERROR') {
    errors.push({ path, message, code });
}
function valString(value, path, errors, opts = {}) {
    if (typeof value !== 'string') {
        if (opts.required)
            addError(errors, path, 'This field is required.');
        return '';
    }
    const out = sanitizePlainText(value, opts.max ?? 5000);
    if (opts.required && out.trim() === '') {
        addError(errors, path, 'This field is required.');
    }
    return out;
}
function valBoolean(value, path, errors, fallback = false) {
    if (typeof value !== 'boolean') {
        addError(errors, path, 'This field must be true or false.');
        return fallback;
    }
    return value;
}
function valNumber(value, path, errors, opts = {}) {
    const fallback = opts.fallback ?? opts.min ?? 0;
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        if (opts.required)
            addError(errors, path, 'This field must be a number.');
        return fallback;
    }
    if (opts.min !== undefined && value < opts.min) {
        addError(errors, path, `This field must be at least ${opts.min}.`);
        return fallback;
    }
    if (opts.max !== undefined && value > opts.max) {
        addError(errors, path, `This field must be no more than ${opts.max}.`);
        return fallback;
    }
    return opts.integer ? Math.round(value) : value;
}
function valOneOf(value, path, errors, allowed, fallback) {
    if (typeof value !== 'string' || !allowed.includes(value)) {
        addError(errors, path, 'This field has an invalid value.');
        return fallback;
    }
    return value;
}
function valUrl(value, path, errors, opts = {}) {
    if (typeof value !== 'string') {
        if (opts.required)
            addError(errors, path, 'This field is required.');
        return '';
    }
    const trimmed = value.trim();
    if (trimmed === '') {
        if (opts.required)
            addError(errors, path, 'This field is required.');
        return '';
    }
    const clean = sanitizeUrl(trimmed, { allowRelative: true });
    if (clean === '') {
        addError(errors, path, 'Links must use http, https, mailto or tel, or be a safe on-page anchor.');
        return '';
    }
    return clean;
}
function valRichText(value, path, errors, opts = {}) {
    if (!Array.isArray(value)) {
        if (opts.required)
            addError(errors, path, 'This field must be rich text.');
        return [];
    }
    return sanitizeRichText(value);
}
function valAssetId(value, path, errors) {
    if (value === null || value === undefined || value === '')
        return null;
    if (typeof value !== 'string') {
        addError(errors, path, 'Image asset must be an id or null.');
        return null;
    }
    const clean = sanitizePlainText(value, 100);
    if (!/^[A-Za-z0-9_-]+$/.test(clean)) {
        addError(errors, path, 'Image asset id is invalid.');
        return null;
    }
    return clean;
}
function valImageRef(value, path, errors) {
    if (!isRecord(value)) {
        addError(errors, path, 'Image reference must be an object.');
        return { assetId: null, alt: '' };
    }
    const assetId = valAssetId(value.assetId, `${path}.assetId`, errors);
    const alt = valString(value.alt, `${path}.alt`, errors, { max: 500 });
    return { assetId, alt };
}
function valLink(value, path, errors) {
    if (!isRecord(value)) {
        addError(errors, path, 'Link must be an object.');
        return { label: '', href: '' };
    }
    const label = valString(value.label, `${path}.label`, errors, { required: true, max: 200 });
    const href = valUrl(value.href, `${path}.href`, errors, { required: true });
    return { label, href };
}
function valOptionalLink(value, path, errors) {
    if (value === null || value === undefined)
        return null;
    return valLink(value, path, errors);
}
function valLinksArray(value, path, errors) {
    if (!Array.isArray(value)) {
        addError(errors, path, 'This field must be a list of links.');
        return [];
    }
    return value.slice(0, 20).map((item, index) => valLink(item, `${path}[${index}]`, errors));
}
function valStringArray(value, path, errors, maxLength = 200) {
    if (!Array.isArray(value)) {
        addError(errors, path, 'This field must be a list of text values.');
        return [];
    }
    return value.slice(0, 100).map((item, index) => valString(item, `${path}[${index}]`, errors, { required: true, max: maxLength }));
}
function valStringOrNumber(value, _path, _errors) {
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}
function valCssColor(value, path, errors, fallback) {
    if (typeof value !== 'string') {
        addError(errors, path, 'Colour must be a hex value.');
        return fallback;
    }
    const clean = sanitizeCssColor(value);
    if (clean === null) {
        addError(errors, path, 'Colour must be a hex value such as #13295E.');
        return fallback;
    }
    return clean;
}
function valFont(value, path, errors, fallback) {
    if (typeof value !== 'string') {
        addError(errors, path, 'Font must be a text value.');
        return fallback;
    }
    return sanitizeFontFamily(value) || fallback;
}
function valCssLength(value, path, errors, fallback) {
    if (typeof value !== 'string' || !isCssLength(value)) {
        addError(errors, path, 'This field must be a CSS length such as 4.5rem.');
        return fallback;
    }
    return value;
}
function validateMeta(value, errors) {
    if (!isRecord(value)) {
        addError(errors, 'meta', 'Site metadata must be an object.');
        return { title: '', description: '', language: 'en' };
    }
    const title = valString(value.title, 'meta.title', errors, { required: true, max: 160 });
    const description = valString(value.description, 'meta.description', errors, { max: 500 });
    const language = valString(value.language, 'meta.language', errors, { required: true, max: 10 }).toLowerCase() || 'en';
    return { title, description, language };
}
function validateTheme(value, errors) {
    if (!isRecord(value)) {
        addError(errors, 'theme', 'Theme must be an object.');
        return defaultTheme;
    }
    const themeName = valString(value.name, 'theme.name', errors, { max: 120 });
    const paletteValue = isRecord(value.palette) ? value.palette : {};
    const palette = {
        background: valCssColor(paletteValue.background, 'theme.palette.background', errors, defaultTheme.palette.background),
        surface: valCssColor(paletteValue.surface, 'theme.palette.surface', errors, defaultTheme.palette.surface),
        text: valCssColor(paletteValue.text, 'theme.palette.text', errors, defaultTheme.palette.text),
        heading: valCssColor(paletteValue.heading, 'theme.palette.heading', errors, defaultTheme.palette.heading),
        accent: valCssColor(paletteValue.accent, 'theme.palette.accent', errors, defaultTheme.palette.accent),
        accentText: valCssColor(paletteValue.accentText, 'theme.palette.accentText', errors, defaultTheme.palette.accentText),
        muted: valCssColor(paletteValue.muted, 'theme.palette.muted', errors, defaultTheme.palette.muted),
        border: valCssColor(paletteValue.border, 'theme.palette.border', errors, defaultTheme.palette.border)
    };
    const fontsValue = isRecord(value.fonts) ? value.fonts : {};
    const fonts = {
        heading: valFont(fontsValue.heading, 'theme.fonts.heading', errors, defaultTheme.fonts.heading),
        body: valFont(fontsValue.body, 'theme.fonts.body', errors, defaultTheme.fonts.body),
        mono: valFont(fontsValue.mono, 'theme.fonts.mono', errors, defaultTheme.fonts.mono)
    };
    const radiusValue = isRecord(value.radius) ? value.radius : {};
    const radius = {
        sm: valCssLength(radiusValue.sm, 'theme.radius.sm', errors, defaultTheme.radius.sm),
        md: valCssLength(radiusValue.md, 'theme.radius.md', errors, defaultTheme.radius.md),
        lg: valCssLength(radiusValue.lg, 'theme.radius.lg', errors, defaultTheme.radius.lg)
    };
    const spacingValue = isRecord(value.spacing) ? value.spacing : {};
    const spacing = {
        section: valCssLength(spacingValue.section, 'theme.spacing.section', errors, defaultTheme.spacing.section),
        container: valCssLength(spacingValue.container, 'theme.spacing.container', errors, defaultTheme.spacing.container)
    };
    return { name: themeName || defaultTheme.name, palette, fonts, radius, spacing };
}
function validateHeaderProps(props, path, errors) {
    return {
        brand: valString(props.brand, `${path}.brand`, errors, { required: true, max: 120 }),
        nav: valLinksArray(props.nav, `${path}.nav`, errors),
        cta: valOptionalLink(props.cta, `${path}.cta`, errors)
    };
}
function validateHeroProps(props, path, errors) {
    return {
        eyebrow: valString(props.eyebrow, `${path}.eyebrow`, errors, { required: true, max: 120 }),
        title: valString(props.title, `${path}.title`, errors, { required: true, max: 200 }),
        lead: valString(props.lead, `${path}.lead`, errors, { required: true, max: 600 }),
        primaryCta: valOptionalLink(props.primaryCta, `${path}.primaryCta`, errors),
        secondaryCta: valOptionalLink(props.secondaryCta, `${path}.secondaryCta`, errors),
        image: valImageRef(props.image, `${path}.image`, errors)
    };
}
function validateTextProps(props, path, errors) {
    return {
        heading: valString(props.heading, `${path}.heading`, errors, { required: true, max: 200 }),
        body: valRichText(props.body, `${path}.body`, errors, { required: true }),
        align: valOneOf(props.align, `${path}.align`, errors, ['left', 'center', 'right'], 'left')
    };
}
function validateImageTextProps(props, path, errors) {
    return {
        image: valImageRef(props.image, `${path}.image`, errors),
        heading: valString(props.heading, `${path}.heading`, errors, { required: true, max: 200 }),
        body: valRichText(props.body, `${path}.body`, errors, { required: true }),
        imagePosition: valOneOf(props.imagePosition, `${path}.imagePosition`, errors, ['left', 'right'], 'left'),
        cta: valOptionalLink(props.cta, `${path}.cta`, errors)
    };
}
function validateFeatureGridProps(props, path, errors) {
    if (!Array.isArray(props.items)) {
        addError(errors, `${path}.items`, 'Feature grid items must be a list.');
    }
    const items = Array.isArray(props.items)
        ? props.items.slice(0, 50).map((item, index) => {
            if (!isRecord(item)) {
                addError(errors, `${path}.items[${index}]`, 'Feature item must be an object.');
                return { icon: 'none', title: '', text: '' };
            }
            return {
                icon: valOneOf(item.icon, `${path}.items[${index}].icon`, errors, ['anchor', 'food', 'spark', 'leaf', 'wrench', 'heart', 'star', 'none'], 'none'),
                title: valString(item.title, `${path}.items[${index}].title`, errors, { required: true, max: 200 }),
                text: valString(item.text, `${path}.items[${index}].text`, errors, { required: true, max: 600 })
            };
        })
        : [];
    return {
        heading: valString(props.heading, `${path}.heading`, errors, { required: true, max: 200 }),
        body: valRichText(props.body, `${path}.body`, errors, { required: true }),
        columns: Number(valOneOf(props.columns === undefined ? undefined : String(props.columns), `${path}.columns`, errors, ['2', '3', '4'], '3')),
        items
    };
}
function listOf(value, path, errors, what) {
    if (!Array.isArray(value)) {
        addError(errors, path, `${what} must be a list.`);
        return [];
    }
    return value.slice(0, 50).map((item, index) => {
        if (!isRecord(item)) {
            addError(errors, `${path}[${index}]`, `${what} item must be an object.`);
            return {};
        }
        return item;
    });
}
function validateStatsProps(props, path, errors) {
    return {
        heading: valString(props.heading, `${path}.heading`, errors, { max: 200 }),
        items: listOf(props.items, `${path}.items`, errors, 'Stats').map((item, index) => ({
            value: valString(item.value, `${path}.items[${index}].value`, errors, { required: true, max: 40 }),
            label: valString(item.label, `${path}.items[${index}].label`, errors, { required: true, max: 140 })
        }))
    };
}
function validateStepsProps(props, path, errors) {
    return {
        heading: valString(props.heading, `${path}.heading`, errors, { required: true, max: 200 }),
        body: valRichText(props.body, `${path}.body`, errors, { required: false }),
        items: listOf(props.items, `${path}.items`, errors, 'Steps').map((item, index) => ({
            title: valString(item.title, `${path}.items[${index}].title`, errors, { required: true, max: 200 }),
            text: valString(item.text, `${path}.items[${index}].text`, errors, { max: 600 })
        }))
    };
}
function validateQuoteProps(props, path, errors) {
    return {
        text: valString(props.text, `${path}.text`, errors, { required: true, max: 400 }),
        author: valString(props.author, `${path}.author`, errors, { max: 120 }),
        role: valString(props.role, `${path}.role`, errors, { max: 160 })
    };
}
function validateBannerProps(props, path, errors) {
    return {
        text: valString(props.text, `${path}.text`, errors, { required: true, max: 240 }),
        cta: valOptionalLink(props.cta, `${path}.cta`, errors)
    };
}
function validateGalleryProps(props, path, errors) {
    const images = Array.isArray(props.images)
        ? props.images.slice(0, 50).map((item, index) => valImageRef(item, `${path}.images[${index}]`, errors))
        : [];
    return {
        heading: valString(props.heading, `${path}.heading`, errors, { required: true, max: 200 }),
        columns: Number(valOneOf(props.columns === undefined ? undefined : String(props.columns), `${path}.columns`, errors, ['2', '3', '4'], '3')),
        images
    };
}
function validatePricingProps(props, path, errors) {
    const items = Array.isArray(props.items)
        ? props.items.slice(0, 20).map((item, index) => {
            if (!isRecord(item)) {
                addError(errors, `${path}.items[${index}]`, 'Pricing item must be an object.');
                return { name: '', price: '', unit: '', features: [], highlighted: false, cta: null };
            }
            return {
                name: valString(item.name, `${path}.items[${index}].name`, errors, { required: true, max: 120 }),
                price: valString(item.price, `${path}.items[${index}].price`, errors, { required: true, max: 40 }),
                unit: valString(item.unit, `${path}.items[${index}].unit`, errors, { max: 40 }),
                features: valStringArray(item.features, `${path}.items[${index}].features`, errors, 300),
                highlighted: valBoolean(item.highlighted, `${path}.items[${index}].highlighted`, errors, false),
                cta: valOptionalLink(item.cta, `${path}.items[${index}].cta`, errors)
            };
        })
        : [];
    return {
        heading: valString(props.heading, `${path}.heading`, errors, { required: true, max: 200 }),
        description: valString(props.description, `${path}.description`, errors, { required: true, max: 600 }),
        items
    };
}
function validateTestimonialsProps(props, path, errors) {
    const items = Array.isArray(props.items)
        ? props.items.slice(0, 50).map((item, index) => {
            if (!isRecord(item)) {
                addError(errors, `${path}.items[${index}]`, 'Testimonial item must be an object.');
                return { quote: '', name: '', role: '' };
            }
            return {
                quote: valString(item.quote, `${path}.items[${index}].quote`, errors, { required: true, max: 800 }),
                name: valString(item.name, `${path}.items[${index}].name`, errors, { required: true, max: 120 }),
                role: valString(item.role, `${path}.items[${index}].role`, errors, { required: true, max: 120 })
            };
        })
        : [];
    return {
        heading: valString(props.heading, `${path}.heading`, errors, { required: true, max: 200 }),
        items
    };
}
function validateMenuListProps(props, path, errors) {
    const sections = Array.isArray(props.sections)
        ? props.sections.slice(0, 20).map((section, sectionIndex) => {
            if (!isRecord(section)) {
                addError(errors, `${path}.sections[${sectionIndex}]`, 'Menu section must be an object.');
                return { name: '', items: [] };
            }
            const items = Array.isArray(section.items)
                ? section.items.slice(0, 100).map((item, itemIndex) => {
                    if (!isRecord(item)) {
                        addError(errors, `${path}.sections[${sectionIndex}].items[${itemIndex}]`, 'Menu item must be an object.');
                        return { name: '', description: '', price: '' };
                    }
                    return {
                        name: valString(item.name, `${path}.sections[${sectionIndex}].items[${itemIndex}].name`, errors, { required: true, max: 200 }),
                        description: valString(item.description, `${path}.sections[${sectionIndex}].items[${itemIndex}].description`, errors, { required: true, max: 400 }),
                        price: valString(item.price, `${path}.sections[${sectionIndex}].items[${itemIndex}].price`, errors, { required: true, max: 40 })
                    };
                })
                : [];
            return {
                name: valString(section.name, `${path}.sections[${sectionIndex}].name`, errors, { required: true, max: 140 }),
                items
            };
        })
        : [];
    return {
        heading: valString(props.heading, `${path}.heading`, errors, { required: true, max: 200 }),
        sections
    };
}
function validateOpeningHoursProps(props, path, errors) {
    const hours = Array.isArray(props.hours)
        ? props.hours.slice(0, 50).map((hour, index) => {
            if (!isRecord(hour)) {
                addError(errors, `${path}.hours[${index}]`, 'Hours item must be an object.');
                return { days: '', time: '' };
            }
            return {
                days: valString(hour.days, `${path}.hours[${index}].days`, errors, { required: true, max: 140 }),
                time: valString(hour.time, `${path}.hours[${index}].time`, errors, { required: true, max: 140 })
            };
        })
        : [];
    return {
        heading: valString(props.heading, `${path}.heading`, errors, { required: true, max: 200 }),
        note: valString(props.note, `${path}.note`, errors, { required: true, max: 600 }),
        hours
    };
}
function validateContactProps(props, path, errors) {
    return {
        heading: valString(props.heading, `${path}.heading`, errors, { required: true, max: 200 }),
        body: valString(props.body, `${path}.body`, errors, { required: true, max: 600 }),
        buttonLabel: valString(props.buttonLabel, `${path}.buttonLabel`, errors, { required: true, max: 120 })
    };
}
function validateMapAddressProps(props, path, errors) {
    return {
        heading: valString(props.heading, `${path}.heading`, errors, { required: true, max: 200 }),
        address: valString(props.address, `${path}.address`, errors, { required: true, max: 600 }),
        mapUrl: valUrl(props.mapUrl, `${path}.mapUrl`, errors, { required: true }),
        buttonLabel: valString(props.buttonLabel, `${path}.buttonLabel`, errors, { required: true, max: 120 })
    };
}
function validateFaqProps(props, path, errors) {
    const items = Array.isArray(props.items)
        ? props.items.slice(0, 100).map((item, index) => {
            if (!isRecord(item)) {
                addError(errors, `${path}.items[${index}]`, 'FAQ item must be an object.');
                return { question: '', answer: '' };
            }
            return {
                question: valString(item.question, `${path}.items[${index}].question`, errors, { required: true, max: 400 }),
                answer: valString(item.answer, `${path}.items[${index}].answer`, errors, { required: true, max: 1200 })
            };
        })
        : [];
    return {
        heading: valString(props.heading, `${path}.heading`, errors, { required: true, max: 200 }),
        items
    };
}
function validateCtaProps(props, path, errors) {
    return {
        title: valString(props.title, `${path}.title`, errors, { required: true, max: 200 }),
        body: valString(props.body, `${path}.body`, errors, { required: true, max: 600 }),
        cta: valLink(props.cta, `${path}.cta`, errors),
        secondaryCta: valOptionalLink(props.secondaryCta, `${path}.secondaryCta`, errors),
        background: valOneOf(props.background, `${path}.background`, errors, ['accent', 'ink', 'paper'], 'accent')
    };
}
function validateSocialLinksProps(props, path, errors) {
    return {
        heading: valString(props.heading, `${path}.heading`, errors, { required: true, max: 200 }),
        items: valLinksArray(props.items, `${path}.items`, errors)
    };
}
function validateFooterProps(props, path, errors) {
    return {
        brand: valString(props.brand, `${path}.brand`, errors, { required: true, max: 120 }),
        tagline: valString(props.tagline, `${path}.tagline`, errors, { required: true, max: 300 }),
        links: valLinksArray(props.links, `${path}.links`, errors),
        copyright: valString(props.copyright, `${path}.copyright`, errors, { required: true, max: 300 })
    };
}
function validateSpacerProps(props, path, errors) {
    return {
        height: valNumber(props.height, `${path}.height`, errors, { required: true, min: 0, max: 600, integer: true, fallback: 48 }),
        divider: valBoolean(props.divider, `${path}.divider`, errors, false)
    };
}
const DESIGN_EXTRA_LIMIT = 12;
/** Sections an owner added to a designed template: allowed block types only, each validated like a normal block. */
function validateDesignExtras(value, sectionCount, path, errors) {
    if (value === undefined)
        return [];
    if (!Array.isArray(value)) {
        addError(errors, path, 'Added sections must be a list.');
        return [];
    }
    const seen = new Set();
    const out = [];
    value.slice(0, DESIGN_EXTRA_LIMIT).forEach((raw, index) => {
        const p = `${path}[${index}]`;
        if (!isRecord(raw)) {
            addError(errors, p, 'An added section must be an object.');
            return;
        }
        const id = typeof raw.id === 'string' && /^[A-Za-z0-9_-]{1,40}$/.test(raw.id) ? raw.id : '';
        if (!id || seen.has(id)) {
            addError(errors, `${p}.id`, 'Each added section needs its own id.');
            return;
        }
        seen.add(id);
        const type = raw.type;
        if (typeof type !== 'string' || !DESIGN_EXTRA_TYPES.includes(type)) {
            addError(errors, `${p}.type`, 'That kind of section cannot be added to a template.');
            return;
        }
        const after = typeof raw.after === 'number' && Number.isInteger(raw.after) && raw.after >= 0 && raw.after < sectionCount ? raw.after : null;
        if (after === null) {
            addError(errors, `${p}.after`, 'Choose a section on the page to place this under.');
            return;
        }
        const cleanProps = validateBlockProps(type, isRecord(raw.props) ? raw.props : {}, `${p}.props`, errors);
        const style = validateBlockStyle(raw.style);
        out.push({ id, type, props: cleanProps, after, ...(style ? { style } : {}) });
    });
    return out;
}
function validateDesignedProps(props, path, errors) {
    const designId = typeof props.designId === 'string' ? props.designId : '';
    const entry = designManifestData.find((d) => d.id === designId);
    if (!/^design-\d{2}$/.test(designId) || !entry) {
        addError(errors, `${path}.designId`, 'Unknown design.');
    }
    const texts = {};
    if (props.texts !== undefined && !isRecord(props.texts)) {
        addError(errors, `${path}.texts`, 'texts must be an object.');
    }
    else if (isRecord(props.texts)) {
        for (const [key, value] of Object.entries(props.texts)) {
            if (isValidDesignText(key, value))
                texts[key] = value;
            else
                addError(errors, `${path}.texts.${key}`, 'Invalid text edit.');
        }
    }
    const vars = {};
    if (props.vars !== undefined && !isRecord(props.vars)) {
        addError(errors, `${path}.vars`, 'vars must be an object.');
    }
    else if (isRecord(props.vars)) {
        for (const [key, value] of Object.entries(props.vars)) {
            if (isValidDesignVar(key, value))
                vars[key] = value.trim();
            else
                addError(errors, `${path}.vars.${key}`, 'Invalid colour.');
        }
    }
    const extras = normalizeDesignExtras(props, entry?.sections?.length ?? 100, entry?.data ?? []);
    for (const e of extras.errors)
        addError(errors, `${path}.${e.path}`, e.message);
    const out = { designId, texts, vars, images: extras.value.images, links: extras.value.links, hidden: extras.value.hidden, order: extras.value.order };
    if (extras.value.fx)
        out.fx = extras.value.fx;
    if (extras.value.fonts)
        out.fonts = extras.value.fonts;
    if (extras.value.data)
        out.data = extras.value.data;
    if (extras.value.favicon)
        out.favicon = extras.value.favicon;
    const business = normalizeBusiness(props.business);
    for (const e of business.errors)
        addError(errors, `${path}.${e.path}`, e.message);
    if (business.value)
        out.business = business.value;
    const added = validateDesignExtras(props.extras, entry?.sections?.length ?? 100, `${path}.extras`, errors);
    if (added.length > 0)
        out.extras = added;
    return out;
}
function validateBlockProps(type, props, path, errors) {
    switch (type) {
        case 'header':
            return validateHeaderProps(props, path, errors);
        case 'hero':
            return validateHeroProps(props, path, errors);
        case 'text':
            return validateTextProps(props, path, errors);
        case 'imageText':
            return validateImageTextProps(props, path, errors);
        case 'featureGrid':
            return validateFeatureGridProps(props, path, errors);
        case 'stats':
            return validateStatsProps(props, path, errors);
        case 'steps':
            return validateStepsProps(props, path, errors);
        case 'quote':
            return validateQuoteProps(props, path, errors);
        case 'banner':
            return validateBannerProps(props, path, errors);
        case 'gallery':
            return validateGalleryProps(props, path, errors);
        case 'pricing':
            return validatePricingProps(props, path, errors);
        case 'testimonials':
            return validateTestimonialsProps(props, path, errors);
        case 'menuList':
            return validateMenuListProps(props, path, errors);
        case 'openingHours':
            return validateOpeningHoursProps(props, path, errors);
        case 'contact':
            return validateContactProps(props, path, errors);
        case 'mapAddress':
            return validateMapAddressProps(props, path, errors);
        case 'faq':
            return validateFaqProps(props, path, errors);
        case 'cta':
            return validateCtaProps(props, path, errors);
        case 'socialLinks':
            return validateSocialLinksProps(props, path, errors);
        case 'footer':
            return validateFooterProps(props, path, errors);
        case 'spacer':
            return validateSpacerProps(props, path, errors);
        case 'designed':
            return validateDesignedProps(props, path, errors);
    }
}
const BLOCK_BACKGROUNDS = ['none', 'surface', 'tint', 'accent'];
const BLOCK_SPACES = ['compact', 'normal', 'roomy'];
/** Section style: only known values survive; anything else is dropped (no error, so older sites keep loading). */
function validateBlockStyle(value) {
    if (!isRecord(value))
        return undefined;
    const style = {};
    if (typeof value.background === 'string' && BLOCK_BACKGROUNDS.includes(value.background) && value.background !== 'none') {
        style.background = value.background;
    }
    if (typeof value.space === 'string' && BLOCK_SPACES.includes(value.space) && value.space !== 'normal') {
        style.space = value.space;
    }
    return style.background || style.space ? style : undefined;
}
function validateBlocks(value, errors) {
    if (!Array.isArray(value)) {
        addError(errors, 'blocks', 'Blocks must be a list.');
        return [];
    }
    const seen = new Set();
    const blocks = [];
    value.slice(0, 200).forEach((rawBlock, index) => {
        const path = `blocks[${index}]`;
        if (!isRecord(rawBlock)) {
            addError(errors, path, 'Block must be an object.');
            return;
        }
        const id = valString(rawBlock.id, `${path}.id`, errors, { required: true, max: 120 });
        if (id && !/^[A-Za-z0-9_-]+$/.test(id)) {
            addError(errors, `${path}.id`, 'Block ids may only contain letters, numbers, hyphens and underscores.');
        }
        if (seen.has(id)) {
            addError(errors, `${path}.id`, 'Block ids must be unique.');
        }
        else if (id) {
            seen.add(id);
        }
        const type = rawBlock.type;
        if (typeof type !== 'string' || !BLOCK_TYPES.includes(type)) {
            addError(errors, `${path}.type`, `Unknown block type: ${String(type)}`, 'UNKNOWN_BLOCK_TYPE');
            return;
        }
        const props = isRecord(rawBlock.props) ? rawBlock.props : {};
        const cleanProps = validateBlockProps(type, props, `${path}.props`, errors);
        const style = type === 'header' || type === 'footer' || type === 'spacer' || type === 'designed' ? undefined : validateBlockStyle(rawBlock.style);
        blocks.push({ id, type: type, props: cleanProps, ...(style ? { style } : {}) });
    });
    return blocks;
}
export function validateSite(input) {
    const errors = [];
    if (!isRecord(input)) {
        return {
            ok: false,
            errors: [{ path: '', message: 'Site must be an object.', code: 'VALIDATION_ERROR' }]
        };
    }
    if (input.version !== 1) {
        addError(errors, 'version', 'Only site version 1 is supported.');
    }
    const meta = validateMeta(input.meta, errors);
    const theme = validateTheme(input.theme, errors);
    const blocks = validateBlocks(input.blocks, errors);
    if (blocks.some((b) => b.type === 'designed') && blocks.length !== 1) {
        addError(errors, 'blocks', 'A designed template must be the only block on the page.');
    }
    if (errors.length > 0) {
        return { ok: false, errors };
    }
    return {
        ok: true,
        value: {
            version: 1,
            meta,
            theme,
            blocks
        }
    };
}
//# sourceMappingURL=validation.js.map